<template>
  <v2-modal
    :show="show"
    title="Sharing & email preview"
    @close="$emit('close')"
  >
    <div class="v2-preview-settings">
      <!-- KP-4586: where the public link's prices come from -->
      <v2-wishlist-price-source-section
        :selection="priceSourceSelection"
        :options="priceSourceOptions"
        :missing-products="missingProducts"
        :source-label="sourceLabel"
        :loading="loadingSource"
        @change="setSource"
      />

      <!-- Price visibility -->
      <section class="v2-preview-settings__section">
        <h4 class="v2-preview-settings__heading">
          Prices recipients can see
        </h4>
        <p class="v2-preview-settings__hint">
          Applies to the public link, the wishlist you share by email, and the
          preview inside an outreach sequence.
        </p>

        <!-- V2Checkbox takes `value` and emits `input`, not checked/change. -->
        <label class="v2-preview-settings__toggle">
          <v2-checkbox
            :value="wholesale"
            @input="togglePrice('wholesale')"
          />
          <span>
            <strong>Wholesale (WSP)</strong>
            <em>Hidden by default — turning this on shows buyers your wholesale price.</em>
          </span>
        </label>

        <label class="v2-preview-settings__toggle">
          <v2-checkbox
            :value="retail"
            @input="togglePrice('retail')"
          />
          <span>
            <strong>Retail (RRP)</strong>
            <em>Turn off to share the range without revealing pricing yet.</em>
          </span>
        </label>
      </section>

      <!-- Products in the email preview -->
      <section class="v2-preview-settings__section">
        <h4 class="v2-preview-settings__heading">
          Products in the email preview
        </h4>
        <p class="v2-preview-settings__hint">
          Drag to reorder — the email shows them in this order, {{ rowCapacity }}
          per row, up to {{ maxPreviewProducts }}.
          <span v-if="selectedIds.length">
            {{ selectedIds.length }} chosen, filling {{ rowCount }}
            {{ rowCount === 1 ? 'row' : 'rows' }}.
          </span>
        </p>

        <!--
          Chosen, in render order. Drag to reorder; each row is also focusable
          and responds to Up/Down, so reordering is not mouse-only — a keyboard
          or screen-reader user has no other way to set the order the email
          renders in.
        -->
        <draggable
          v-if="selectedIds.length"
          tag="ol"
          class="v2-preview-settings__chosen"
          :list="selectedIds"
          handle=".v2-preview-settings__grip"
        >
          <li
            v-for="(id, index) in selectedIds"
            :key="id"
            tabindex="0"
            :aria-label="`${nameFor(id)}, position ${index + 1} of ${selectedIds.length}. Use up and down arrows to reorder.`"
            @keydown.up.prevent="moveProduct(id, -1)"
            @keydown.down.prevent="moveProduct(id, 1)"
          >
            <span
              class="v2-preview-settings__grip"
              aria-hidden="true"
            >⠿</span>
            <span class="v2-preview-settings__position">{{ index + 1 }}</span>
            <span class="v2-preview-settings__chosen-name">{{ nameFor(id) }}</span>
            <button
              type="button"
              aria-label="Remove"
              @click="toggleProduct(id)"
            >
              ×
            </button>
          </li>
        </draggable>
        <p
          v-else
          class="v2-preview-settings__empty"
        >
          Nothing chosen — the email shows the first {{ defaultPreviewProducts }}
          products of this wishlist, in its own order.
        </p>

        <!-- Candidates. A wishlist can hold hundreds of styles, so the grid is
             searchable by name and style code — scrolling is not a way to find
             a specific product. -->
        <div class="v2-preview-settings__search">
          <input
            v-model="candidateSearch"
            type="search"
            class="v2-preview-settings__search-input"
            placeholder="Search by product or style code"
            aria-label="Search products to add to the email preview"
          >
          <span class="v2-preview-settings__search-count">
            {{ searchCountLabel }}
          </span>
        </div>

        <div
          v-if="visibleCandidates.length"
          class="v2-preview-settings__candidates"
        >
          <button
            v-for="product in visibleCandidates"
            :key="product.id"
            type="button"
            class="v2-preview-settings__candidate"
            :class="{ 'is-selected': selectedIds.includes(product.id) }"
            :aria-pressed="selectedIds.includes(product.id) ? 'true' : 'false'"
            @click="toggleProduct(product.id)"
          >
            <img
              v-if="product.image"
              :src="product.image"
              :alt="product.name"
            >
            <span>{{ product.name }}</span>
          </button>
        </div>
        <p
          v-else-if="candidateSearch.trim()"
          class="v2-preview-settings__empty"
        >
          No product matches “{{ candidateSearch }}”.
          <button
            type="button"
            class="v2-preview-settings__link"
            @click="candidateSearch = ''"
          >
            Clear the search
          </button>
        </p>
        <p
          v-else
          class="v2-preview-settings__empty"
        >
          This wishlist has no products to choose from yet.
        </p>
      </section>

      <!-- Preview -->
      <section class="v2-preview-settings__section">
        <div class="v2-preview-settings__preview-head">
          <h4 class="v2-preview-settings__heading">
            Email preview
          </h4>
          <v2-button
            variant="outline"
            size="sm"
            :disabled="loadingPreview"
            @click="onPreview"
          >
            {{ loadingPreview ? 'Loading…' : 'Preview' }}
          </v2-button>
        </div>
        <p class="v2-preview-settings__hint">
          Rendered by the same code that builds the real email, from your saved
          settings.
        </p>

        <div
          v-if="previewError"
          class="v2-preview-settings__preview-error"
        >
          {{ previewError }}
        </div>
        <!--
          Rendered in a sandboxed iframe rather than with v-html, for two
          reasons. It is server-authored markup, and `sandbox` without
          allow-scripts means it can never execute in the app's origin. And an
          email client applies none of this app's CSS — injecting the block into
          the page would restyle it, so the preview would stop being a preview.
        -->
        <iframe
          v-else-if="previewHtml"
          class="v2-preview-settings__preview"
          title="Email preview"
          sandbox=""
          :srcdoc="previewDocument"
        />
      </section>
    </div>

    <template #footer>
      <v2-button
        variant="outline"
        @click="$emit('close')"
      >
        Cancel
      </v2-button>
      <v2-button
        :disabled="saving || savingSource || !sourceValid"
        @click="onSave"
      >
        {{ saving || savingSource ? 'Saving…' : 'Save' }}
      </v2-button>
    </template>
  </v2-modal>
</template>

<script>
import { ref, toRef, watch } from '@vue/composition-api'
import draggable from 'vuedraggable'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import {
  DEFAULT_PREVIEW_PRODUCTS,
  MAX_PREVIEW_PRODUCTS,
  PREVIEW_ROW_CAPACITY,
  useWishlistPreviewSettings,
} from '@/composables/useWishlistPreviewSettings'
import { useWishlistPriceSource } from '@/composables/useWishlistPriceSource'
import V2WishlistPriceSourceSection from './V2WishlistPriceSourceSection.vue'

export default {
  name: 'V2WishlistPreviewSettingsModal',
  components: {
    draggable, V2Modal, V2Button, V2Checkbox, V2WishlistPriceSourceSection,
  },
  props: {
    show: { type: Boolean, default: false },
    wishlist: { type: Object, default: () => ({}) },
    // The wishlist's products, as the page loads them — each either a wishlist
    // product wrapping `productInfo` or a plain product. See toCandidate.
    products: { type: Array, default: () => [] },
  },
  setup(props, { emit }) {
    const wishlistRef = toRef(props, 'wishlist')
    const settings = useWishlistPreviewSettings(wishlistRef)
    const priceSource = useWishlistPriceSource(wishlistRef)
    const candidateSearch = ref('')

    // Re-seed each time the dialog opens so a cancelled edit never persists
    // into the next open.
    watch(() => props.show, isOpen => {
      if (!isOpen) return
      settings.hydrate()
      priceSource.loadSourceOptions()
      candidateSearch.value = ''
    }, { immediate: true })

    // Build the payload BEFORE saving. save() clears the touched flags on
    // success, so calling buildPayload() afterwards always omits
    // priceVisibility — the event would describe something other than what was
    // persisted.
    async function persist() {
      const persisted = settings.buildPayload()
      const ok = await settings.save()
      if (ok) emit('saved', persisted)

      return ok
    }

    // The price source saves on its own endpoint, and only when it changed.
    // Products it cannot price are listed in the dialog, which stays open.
    // Resolves false when either save failed.
    async function persistAll() {
      // Visibility decides which book prices a buyer sees, so a visibility
      // change rechecks the missing products even if the source did not change.
      const recheck = Boolean(settings.buildPayload().priceVisibility)
      if (!(await persist())) return false

      return (await priceSource.saveSource({ recheck })) !== null
    }

    async function onSave() {
      await persistAll()
    }

    // Preview reads STORED settings, so save first — otherwise the brand would
    // be shown the previous state and reasonably conclude their edit was lost.
    //
    // It announces the save exactly as the Save button does. The parent refetches
    // the wishlist on `saved`, and the dialog re-seeds from that prop every time
    // it opens; stay silent here and a preview-then-close would show the previous
    // selection on reopen, reading as if the save had been lost.
    //
    // The price source is saved too: the preview is priced from it (KP-4586).
    async function onPreview() {
      if (await persistAll()) await settings.loadPreview()
    }

    return {
      ...settings,
      ...priceSource,
      onSave,
      onPreview,
      candidateSearch,
      maxPreviewProducts: MAX_PREVIEW_PRODUCTS,
      rowCapacity: PREVIEW_ROW_CAPACITY,
      defaultPreviewProducts: DEFAULT_PREVIEW_PRODUCTS,
    }
  },
  computed: {
    /**
     * A bare white document, as an email client would present it — no app CSS.
     *
     * The anchors lose their href HERE ONLY. Activating one navigated the
     * sandboxed iframe to the wishlist page, which cannot run scripts in that
     * sandbox, so the preview was replaced by the app's "enable JavaScript"
     * screen. It also goes nowhere useful: the brand is already looking at
     * that wishlist.
     *
     * Dropping the href rather than hiding it behind `pointer-events` is what
     * makes it genuinely inert — an anchor without one has no default action
     * and takes no focus, so keyboard and middle-click cannot reach it either.
     * The styling is inline on each anchor, so everything still looks exactly
     * as the recipient will see it, which is the point of previewing.
     *
     * The block is rendered again server-side for the real email, so what gets
     * sent still links normally — this copy is a picture of it.
     */
    previewDocument() {
      const unlinked = this.previewHtml.replace(/\shref="[^"]*"/g, '')

      return '<!doctype html><html><head><meta charset="utf-8">'
        + '<style>body{margin:0;padding:12px;background:#fff;}</style>'
        + `</head><body>${unlinked}</body></html>`
    },
    candidates() {
      return this.products.map(this.toCandidate).filter(candidate => candidate.id)
    },

    visibleCandidates() {
      const term = this.candidateSearch.trim().toLowerCase()
      if (!term) return this.candidates

      return this.candidates.filter(product => `${product.name} ${product.styleCode}`
        .toLowerCase()
        .includes(term))
    },

    searchCountLabel() {
      const total = this.candidates.length
      if (!this.candidateSearch.trim()) {
        return `${total} ${total === 1 ? 'product' : 'products'}`
      }

      return `${this.visibleCandidates.length} of ${total}`
    },
  },
  methods: {
    /**
     * A wishlist product carries its details under `productInfo`, but the same
     * list is also handed straight products in places, so each field falls back
     * to the outer object.
     */
    toCandidate(product) {
      // Details usually sit under `productInfo`, but the same list is handed
      // straight products elsewhere, and either shape may key its id as `_id`
      // or `id`. Flatten once and read the merged result.
      const source = { ...product, ...(product.productInfo || {}) }

      return {
        id: source._id || source.id,
        name: source.name,
        // Merchandisers look styles up by code far more often than by name, so
        // it is searchable. It is NOT rendered on the tile — a second text line
        // there stops this dialog painting (see the PR).
        styleCode: source.styleCode || '',
        image: (source.images || [])[0],
      }
    },

    nameFor(id) {
      return this.candidates.find(product => product.id === id)?.name || id
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-preview-settings {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__heading {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  &__hint {
    margin: 0;
    font-size: 0.8rem;
    color: var(--v2-muted-foreground);
  }

  &__toggle {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    cursor: pointer;

    em {
      display: block;
      font-style: normal;
      font-size: 0.8rem;
      color: var(--v2-muted-foreground);
    }
  }

  &__chosen {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8rem;
      padding: 0.3rem 0.5rem;
      border: 1px solid var(--v2-border, rgba(0, 0, 0, 0.08));
      border-radius: var(--v2-radius-md, 6px);
      background: var(--v2-background, #fff);

      // Keyboard reordering needs a visible focus target.
      &:focus-visible {
        outline: 2px solid var(--v2-primary);
        outline-offset: 1px;
      }
    }

    // vuedraggable's placeholder for the row in flight.
    .sortable-ghost {
      opacity: 0.45;
    }

    button {
      border: 0;
      background: transparent;
      cursor: pointer;
      padding: 0 0.25rem;

      &:disabled {
        opacity: 0.3;
        cursor: default;
      }
    }
  }

  // Only the grip starts a drag, so Remove stays clickable and a stray drag on
  // the row does not fight the scroll.
  &__grip {
    cursor: grab;
    color: var(--v2-muted-foreground);
    line-height: 1;
    user-select: none;

    &:active {
      cursor: grabbing;
    }
  }

  &__position {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: var(--v2-primary);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
  }

  &__chosen-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    margin: 0;
    font-size: 0.8rem;
    color: var(--v2-muted-foreground);
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.75rem 0 0.5rem;
  }

  &__search-input {
    flex: 1;
    min-width: 0;
    min-height: 38px;
    padding: 0.5rem 0.75rem;
    font: inherit;
    color: var(--v2-foreground);
    background: var(--v2-surface-2, rgba(255, 255, 255, 0.6));
    border: 1px solid var(--v2-border, rgba(0, 0, 0, 0.1));
    border-radius: var(--v2-radius-md, 6px);

    &:focus {
      outline: none;
      border-color: var(--v2-primary, #6964ed);
    }
  }

  &__search-count {
    flex: none;
    font-size: 0.8rem;
    color: var(--v2-muted-foreground);
    font-variant-numeric: tabular-nums;
  }

  &__link {
    border: 0;
    background: none;
    padding: 0;
    font: inherit;
    color: var(--v2-primary, #6964ed);
    cursor: pointer;
    text-decoration: underline;
  }

  &__candidates {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 0.5rem;
    max-height: 220px;
    overflow-y: auto;
  }

  &__candidate {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.35rem;
    border: 1px solid var(--v2-border, rgba(0, 0, 0, 0.1));
    border-radius: var(--v2-radius-md, 6px);
    background: transparent;
    cursor: pointer;
    font-size: 0.8rem;
    text-align: left;

    img {
      width: 100%;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: 4px;
    }

    &.is-selected {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 1px var(--v2-primary);
    }
  }

  &__preview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  &__preview {
    width: 100%;
    min-height: 260px;
    border: 1px solid var(--v2-border, rgba(0, 0, 0, 0.1));
    border-radius: var(--v2-radius-md, 6px);
    background: #fff;
  }

  &__preview-error {
    font-size: 0.8rem;
    color: var(--v2-destructive);
  }
}
</style>
